<template>
  <section>
    <question v-if="display"></question>
    <interest-adjustment v-if="showAdjustmentModal" :noteId="note.noteId" :isSubmitDisabled="isSubmitDisabled" :submitAction="submitAdjustment" :cancelAction="cleanAdjustmentInformation">
      <div slot="body">
        <p><strong class="text-info">*</strong><small> Required fields</small></p>
        <div class="title-holder">
          <h1>Add new INT-ADJ</h1>
        </div>
        <div class="row">
          <div class="col-md-6">
            <label class="adjustment-label">Adjustment amount<strong class="text-info">*</strong></label>
            <span class="dollar-sign-holder form-control" :class="{'form-control-error': errors.has('interest-amount')}">
              <input class="currency" name="interest-amount" type="number" v-model="fixedAdjustmentAmount" value="2" placeholder="0.00" step="0.01" v-validate="{required: true}"/>
            </span>
            <span v-if="errors.has('interest-amount') || adjustmentAmount == 0" class="error-feedback">The adjustment amount cannot be zero. Please enter a different amount.</span>
          </div>
          <div class="col-md-6">
            <label class="adjustment-label">Date<strong class="text-info">*</strong></label>
            <div class="input-group date">
              <date-picker disabled :value="adjustmentDate" :config="dateConfig" />
              <div class="input-group-addon">
                <span class="fa fa-calendar"></span>
              </div>
            </div>
          </div>
        </div>
        <div class="row row-margin">
          <div class="col-sm-12">
            <label class="adjustment-label">Comment<strong class="text-info">*</strong></label>
            <textarea class="form-control"
              rows="4"
              id="comment"
              name="interest-comment"
              placeholder="Enter your comments here"
              v-validate="{required: true}"
              :class="{'form-control-error': errors.has('interest-comment')}"
              v-model="comment"></textarea>
              <span v-if="errors.has('interest-comment')" class="error-feedback">The comment field cannot be empty. Please enter a comment.</span>
          </div>
        </div>
        <div class="row row-margin">
          <div class="col-md-6">
            <div class="title-holder">
              <h1>Resulting current INT-PAID</h1>
            </div>
            <label class="adjustment-label">Interest paid amount</label>
            <span class="dollar-sign-holder form-control" disabled>
              <input class="currency" name="interest-paid" type="number" v-model="interestPaid" disabled v-validate="{min_value: 0}" />
            </span>
            <span v-if="interestPaid < 0.005" class="error-feedback">This interest payment would be zero or negative once rounded to two decimal places. Please change the adjustment so that the interest payment will be at least $0.01 once rounded to two decimal places.</span>
          </div>
          <div class="col-md-6">
             <div class="title-holder">
              <h1>Resulting current interest accrued</h1>
            </div>
            <label class="adjustment-label">Interest accrued amount</label>
             <span class="dollar-sign-holder form-control" disabled>
              <input class="currency" type="number" v-model="interestAccrued" disabled />
            </span>
          </div>
        </div>
      </div>
    </interest-adjustment>

    <div class="content-wrapper">
      <errors v-if="isVisibleErrorsModal"></errors>
      <information v-if="isInformationModalVisible"></information>
      <edit-transaction-comment
        v-if="isVisibleEditCommentModal"
        v-bind:closeAction="closeEditCommentModal"
        v-bind:transaction="transaction"
        v-bind:loadNoteDetail="loadNoteDetail"
      ></edit-transaction-comment>
      <div class="content-heading">
        All users
        <small data-localize="dashboard.WELCOME"></small>
      </div>

      <breadcrumb class="breadcrumb" v-bind:breadcrumbsData="breadcrumbData" />
      <br>

      <div class="row">
        <spinner v-if="loading"></spinner>
        <div class="col-lg-12">
          <div id="panelChart9" class="panel panel-default">
          <div class="panel-title">
            Note details
          </div>
          <div class="line-separator"></div>
          <div class="bottom-margin">
            <div class="row top-margin">
              <div class="col-md-2 left-margin">
                <div>
                  <label>Id:</label>
                </div>
              </div>
              <div class="col-md-3 left-margin">
                <div>
                <p>{{note.noteId}}</p>
                </div>
              </div>
              <div class="col-md-2 left-margin">
                <div>
                  <label>Current amount:</label>
                </div>
              </div>
              <div class="col-md-3 left-margin">
                <div>
                  <p>{{formatAmount(note.currentBalance)}}</p>
                </div>
              </div>
            </div>
            <div class="row top-margin">
              <div class="col-md-2 left-margin">
                <div>
                  <label>Term:</label>
                </div>
              </div>
              <div class="col-md-3 left-margin">
                <div>
                <p>{{formatTerm(note.interestTerm)}}</p>
                </div>
              </div>
              <div class="col-md-2 left-margin">
                <div>
                  <label>Open Date:</label>
                </div>
              </div>
              <div class="col-md-3 left-margin">
                <div>
                  <p>{{formatDate(note.openedOn)}}</p>
                </div>
              </div>
            </div>
            <div class="row top-margin">
              <div class="col-md-2 left-margin">
                <div>
                  <label>Rate:</label>
                </div>
              </div>
              <div class="col-md-3 left-margin">
                <div>
                <p>{{formatRate(note.interestRate)}}</p>
                </div>
              </div>
              <div class="col-md-2 left-margin">
                <div>
                  <label>Maturity:</label>
                </div>
              </div>
              <div class="col-md-3 left-margin">
                <div>
                  <p>{{formatDate(note.maturityDate)}}</p>
                </div>
              </div>
            </div>
            <div class="row top-margin">
              <div class="col-md-2 left-margin">
                <div>
                  <label>Original amount:</label>
                </div>
              </div>
              <div class="col-md-3 left-margin">
                <div>
                <p>{{formatAmount(note.originalBalance)}}</p>
                </div>
              </div>
              <div class="col-md-2 left-margin">
                <div>
                  <label>Status:</label>
                </div>
              </div>
              <div class="col-md-3 left-margin">
                <div>
                  <p class="label label-danger" v-if="note.status == 'closed'">Closed</p>
                  <p class="label label-danger" v-if="note.status == 'cancelled'">Cancelled</p>
                  <p class="label label-warning" v-if="note.status == 'pending'">Pending</p>
                  <p class="label label-success" v-if="note.status == 'open'">Open</p>
                </div>
              </div>
            </div>
            <div class="row top-margin" v-if="note.cusip">
              <div class="col-md-2 left-margin">
                <div>
                  <label>CUSIP:</label>
                </div>
              </div>
              <div class="col-md-3 left-margin">
                <div>
                  <p>{{note.cusip}}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="row bottom-margin">
            <div class="col-md-10 top-margin bottom-margin">
              <button type="submit" class="btn btn-info btn-lg btn-note-action" v-bind:disabled="isRedeemDisabled" v-on:click="goToRedeem()">Redeem</button>
              <button class="btn btn-info btn-lg btn-note-action" v-bind:disabled="isTransferDisabled" v-on:click="goToTransfer()">Transfer</button>
              <button class="btn btn-info btn-lg btn-note-action" v-bind:disabled="isAddFundsDisabled" v-on:click="goToAddFunds()">Add funds</button>
            </div>
          </div>
          <div v-if="adjustmentWasSuccessfull" class="alert alert-success alert-dismissible" role="alert">
            <button @click="adjustmentWasSuccessfull = false" type="button" class="close" data-dismiss="alert" aria-label="Close"><span aria-hidden="true">&times;</span></button>
            <i class="fa fa-check-circle-o"></i> The interest adjustment was saved successfully.
          </div>
          <div class="line-separator"></div>
          <div class="panel-subtitle bottom-margin">
              Transaction list
          </div>
          <div v-if="note.transactions.length === 0">
            <div class="alert alert-info alert-dismissible" role="alert">
              <p><em class="icon-info"></em> No transactions found.</p>
            </div>
          </div>
          <div v-bind:class="{'table-fixed-header--md cols-7': note.transactions.length > 6}" class="table-responsive bottom-margin">
            <table class="table table-striped table-transaction">
              <thead>
                <tr>
                  <sortable-th
                    :sort-property="sortProperty"
                    :sort-toggle="sortToggle"
                    @handleSortStrategy="sortTransactionsStrategy"
                    property="transactionId"
                    label="Id"
                  />
                  <sortable-th
                    :sort-property="sortProperty"
                    :sort-toggle="sortToggle"
                    @handleSortStrategy="sortTransactionsStrategy"
                    property="type"
                    label="Transaction type"
                  />
                  <sortable-th
                    :sort-property="sortProperty"
                    :sort-toggle="sortToggle"
                    @handleSortStrategy="sortTransactionsStrategy"
                    property="amount"
                    label="Amount"
                  />

                  <sortable-th
                    :sort-property="sortProperty"
                    :sort-toggle="sortToggle"
                    @handleSortStrategy="sortTransactionsStrategy"
                    property="createdAt"
                    label="Date"
                  />

                  <sortable-th
                    :sort-property="sortProperty"
                    :sort-toggle="sortToggle"
                    @handleSortStrategy="sortTransactionsStrategy"
                    property="updatedAt"
                    label="Last updated date"
                  />

                  <sortable-th
                    :sort-property="sortProperty"
                    :sort-toggle="sortToggle"
                    @handleSortStrategy="sortTransactionsStrategy"
                    property="status"
                    label="Status"
                  />

                  <th class="text-center">Comments</th>
                  <th class="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="transaction in orderedTransactions" v-bind:key="transaction.id" v-show="!(transaction.type === 'ACCR-INT' && note.status === 'closed')">
                  <td class="text-center">{{transaction.transactionId}}</td>
                  <td class="text-center">{{formatType(transaction.type, 'admin', transaction.fundingSourceId)}}</td>
                  <td class="text-center">
                      <div class="aligned-amounts-container">
                        <span class="whole">{{getWholeAmount(transaction.amount)}}</span>
                        <span>.</span>
                        <span class="decimal">{{getDecimals(transaction.amount)}}</span>
                      </div>
                  </td>
                  <td class="text-center">{{formatDate(transaction.createdAt)}}</td>
                  <td class="text-center">{{formatDate(transaction.updatedAt)}}</td>
                  <td class="text-center">
                    <span class="label label-danger" v-if="transaction.status == 'CANCELLED'">Cancelled</span>
                    <span class="label label-danger" v-if="transaction.status == 'FAILED'">Failed</span>
                    <span class="label label-warning" v-if="transaction.status == 'PENDING'">Pending</span>
                    <span class="label label-warning" v-if="transaction.status == 'PRE-PENDING'">Pre-pending</span>
                    <span class="label label-success" v-if="transaction.status == 'PROCESSED'">Processed</span>
                    <span class="label label-success" v-if="transaction.status == 'BATCHED'">Batched</span>
                  </td>
                  <td class="text-center">
                    <span
                        class="transaction-comment"
                        v-if="transaction.comment"
                        v-tooltip.top="transaction.comment"
                      >{{transaction.comment}}</span>
                    <button
                      type="button"
                      class="btn btn-details edit-button"
                      v-on:click="openEditCommentModal(transaction)"
                    >
                      <i class="fa fa-pencil" aria-hidden="true"></i>
                    </button>
                  </td>
                  <td class="text-center">
                    <button type="button" class="btn btn-details btn-outline" v-if="canViewRedeemDetail(transaction)"
                      v-on:click="goToViewDetail()">View details</button>
                    <button type="button" class="btn btn-details btn-outline" v-if="canViewTransferDetail(transaction)"
                      v-on:click="goToViewTransferDetail()">View details</button>
                    <button type="button" class="btn btn-details btn-outline" v-if="canViewAddFundsDetail(transaction)"
                      v-on:click="goToAddFunds()">View details</button>
                    <button type="button"
                    class="btn btn-details btn-outline"
                    v-if="canRerunInterestAccrual(transaction)"
                    :disabled="note.status === 'closed'"
                    v-on:click="isRerunInterestAccrualDisabled ? (handleRerunInterestAccrualDisabledShowModal()) : (handleRerunInterestAccrual(note.id))">Re-run interest accrual</button>
                    <button type="button" class="btn btn-details btn-outline" v-if="canTransactionBeFailed(transaction)"
                    v-on:click="failTransaction(transaction.id)">{{markAsFailedMessage(transaction)}}</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="row bottom-margin">
            <div class="col-md-2 top-margin bottom-margin">
              <button type="button" class="btn btn-info btn-lg btn-redeem" @click="showAdjustmentModal=true" :disabled="!isAdjustmentPossible">Interest adjustment</button>
            </div>
          </div>
          <div class="line-separator top-margin"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
  import {
    mapActions,
    mapMutations,
    mapState,
  } from 'vuex';
  import datePicker from 'vue-bootstrap-datetimepicker';
  import Errors from '@/components/modals/Errors';
  import Information from '@/components/modals/Information';
  import EditTransactionComment from '@/components/modals/EditTransactionComment';
  import Question from '@/components/modals/Question';

  import TransactionTypes from '@/data/static/transactionTypes'; // eslint-disable-line no-unused-vars

  import _ from 'lodash';

  import {
    SET_ERRORS_MODAL_INFO,
    SHOW_ERRORS_MODAL,
    SHOW_QUESTION_MODAL,
    HIDE_QUESTION_MODAL,
    SHOW_INFORMATION_MODAL,
  } from '@/store/mutation-types';

  import {
  formatDate,
  formatAmount,
  formatRate,
  formatTerm,
  formatType,
  formatNegativeAmount,
  getAccounTypeProperties,
  orderTransactions,
} from '@/util';

  import spinner from '@/components/widgets/Spinner';

  import InterestAdjustment from '@/components/modals/InterestAdjustment';
  import SortableTh from '@/components/table/SortableTh';
  import breadcrumb from '@/components/Breadcrumb';
  import { UserTypes } from '@/data/static/userTypes';
  import TransactionStatuses from '@/data/static/transactionStatuses';

  const mix = {
    methods: {
      formatDate,
      formatAmount,
      formatRate,
      formatTerm,
      formatType,
      formatNegativeAmount,
    },
  };

  export default {
    name: 'noteDetail',
    beforeMount() {
      this.setParams();
      this.loadData();
    },
    mixins: [mix],
    components: {
      errors: Errors,
      information: Information,
      question: Question,
      'edit-transaction-comment': EditTransactionComment,
      'interest-adjustment': InterestAdjustment,
      'date-picker': datePicker,
      spinner,
      breadcrumb,
      'sortable-th': SortableTh,
    },
    watch: {
      note: function changeNote(newNote) {
        const account = newNote.account;
        const accountObj = {
          accountName: account.accountName || '',
          accountType: account.accountType || '',
          accountId: account.id || '',
        };

        this.breadcrumbData = [];
        this.breadcrumbData.push(
          Object.assign({}, {
            title: 'Search',
            goto: { name: 'Users' },
            selected: false,
          }),
        );
        const params = Object.assign({}, this.$route.params);
        const user = this.user;
        let goto;
        if (user.userType === UserTypes.FINANCIAL_ADVISOR.name) {
          goto = {
            name: 'EditFinancialAdvisor',
            params: {
              name: params.name,
              userId: params.userId,
            },
          };
        } else {
          goto = { name: 'UserDetail', params: { name: params.name, userId: params.userId } };
        }
        this.breadcrumbData.push(
          Object.assign({}, {
            goto,
            title: user.firstName.concat(' ', user.lastName),
            selected: false,
          }),
        );
        const accountTypeProperties = getAccounTypeProperties(accountObj.accountType);
        const gotoAccount = {
          name: accountTypeProperties.component,
          params: {
            accountId: accountObj.accountId,
            name: params.name,
            userId: params.userId,
          },
        };
        this.breadcrumbData.push(
          Object.assign({}, {
            title: accountObj.accountName,
            goto: gotoAccount,
            selected: false,
          }),
        );
        this.breadcrumbData.push(
          Object.assign({}, {
            title: 'Note'.concat(' ', newNote.noteId),
            goto: { name: this.$route.name, params: this.$route.params },
            selected: true,
          }),
        );
      },
    },
    data() {
      return {
        noteId: '',
        userId: '',
        name: '',
        note: {
          transactions: [],
        },
        user: {},
        breadcrumbData: [],
        showAdjustmentModal: false,
        notesError: '',
        comment: '',
        isRedeemDisabled: false,
        isRerunInterestAccrualDisabled: false,
        isTransferDisabled: false,
        isAddFundsDisabled: false,
        loading: false,
        adjustmentAmount: null,
        dateConfig: {
          format: 'MM/DD/YYYY',
        },
        errorOnAdjustment: false,
        adjustmentWasSuccessfull: false,
        minimunInterestPaid: 0.005,
        isVisibleEditCommentModal: false,
        transaction: null,
        sortProperty: 'updatedAt',
        sortToggle: 1,
        orderedTransactions: [],
      };
    },
    computed: {
      ...mapState('information', [
        'isInformationModalVisible',
      ]),
      ...mapState('errors', [
        'isVisibleErrorsModal',
      ]),
      ...mapState('question', [
        'display',
      ]),
      transactionToAdjust() {
        const intPaidTransactions = this.note.transactions.filter(transaction => transaction.type === 'INT-PAID' && transaction.status === 'PRE-PENDING');
        return intPaidTransactions.length === 1
        ? intPaidTransactions[0]
        : this.verifyRedemption(intPaidTransactions);
      },
      isAdjustmentPossible() {
        return _.some(this.note.transactions, { status: 'PRE-PENDING', type: 'INT-PAID' });
      },
      interestAccrued() {
        const accrint = this.note.transactions.filter(transaction =>
          transaction.type === 'ACCRINT')[0];
        const amount = accrint ? accrint.amount : 0;
        return (amount + (parseFloat(this.adjustmentAmount || 0))).toFixed(8);
      },
      interestPaid() {
        const amount = this.transactionToAdjust ? this.transactionToAdjust.amount : 0;
        return (amount + (parseFloat(this.adjustmentAmount || 0))).toFixed(8);
      },
      adjustmentDate() {
        return new Date();
      },
      isSubmitDisabled() {
        return !(Number(this.adjustmentAmount) &&
          this.interestPaid > this.minimunInterestPaid &&
          this.comment);
      },
      fixedAdjustmentAmount: {
        get() {
          if (this.adjustmentAmount) {
            const hasDecimals = this.adjustmentAmount.toString().split('.')[1];
            const decimalPlaces = hasDecimals ? hasDecimals.length : 0;
            return !Number.isInteger(this.adjustmentAmount) && decimalPlaces > 1
              ? parseFloat(this.adjustmentAmount).toFixed(2)
              : this.adjustmentAmount;
          }
          return this.adjustmentAmount;
        },
        set(value) {
          this.adjustmentAmount = value;
        },
      },
      failableTransactions() {
        return [
          TransactionTypes.PAID_INT,
          TransactionTypes.REPAID_ACCT,
          TransactionTypes.REPAID_ACCT_FULL,
          TransactionTypes.REPAID_ACCT_PARTIAL,
        ];
      },
    },
    methods: {
      ...mapActions('note', [
        'getNote',
        'adjustInterestForNote',
        'resetInterestAccrual',
      ]),
      ...mapActions('accountCreation', [
        'getUserDetail',
      ]),
      ...mapMutations('question', {
        hideQuestionModal: HIDE_QUESTION_MODAL,
        showQuestionModal: SHOW_QUESTION_MODAL,
      }),
      markAsFailedMessage(transaction) {
        const baseMessage = 'Mark as failed';
        if (transaction.hasPendingFailureApproval) {
          return `Approve ${baseMessage}`;
        }
        return baseMessage;
      },
      sortTransactionsStrategy(value = undefined) {
        if (value === 'transactionId') {
          this.sortTransactionById('transactionId');
          return;
        } else if (value === 'status') {
          this.sortByStatus();
          return;
        } else if (value) {
          this.sortTransactionsByProperty(value);
          return;
        }
        this.orderedTransactions = orderTransactions(this.note.transactions);
      },
      sortByStatus() {
        this.sortToggle = this.sortProperty === 'status' ? this.sortToggle * -1 : -1;

        this.orderedTransactions = this.note.transactions.sort((a, b) => {
          if (a.status < b.status || (!a.status && b.status)) {
            return -1 * this.sortToggle;
          }
          if (a.status > b.status || (a.status && !b.status)) {
            return 1 * this.sortToggle;
          }
          if (a.createdAt < b.createdAt || (!a.createdAt && b.createdAt)) {
            return 1;
          }
          if (a.createdAt > b.createdAt || (a.createdAt && !b.createdAt)) {
            return -1;
          }
          return 0;
        });

        this.sortProperty = 'status';
      },
      sortTransactionsByProperty(property) {
        this.sortToggle = this.sortProperty === property ? this.sortToggle * -1 : 1;

        this.orderedTransactions = this.note.transactions.sort((a, b) => {
          if (a[property] < b[property] || (!a[property] && b[property])) {
            return -1 * this.sortToggle;
          }
          if (a[property] > b[property] || (a[property] && !b[property])) {
            return 1 * this.sortToggle;
          }
          return 0;
        });

        this.sortProperty = property;
      },
      sortTransactionById(property) {
        this.sortToggle = this.sortProperty === property ? this.sortToggle * -1 : 1;

        this.orderedTransactions = this.note.transactions.sort((a, b) => {
          if (!a[property] && !b[property]) {
            return 0;
          }
          if (!b[property]) {
            return 1 * this.sortToggle;
          }
          if (!a[property]) {
            return -1 * this.sortToggle;
          }
          return (
            a[property].localeCompare(b[property], 'en', { numeric: true }) *
            this.sortToggle
          );
        });

        this.sortProperty = property;
      },
      failTransaction(id) {
        this.$router.push({
          name: 'FailTransaction',
          params: {
            name: this.$route.params.name,
            userId: this.$route.params.userId,
            noteId: this.$route.params.noteId,
            transactionId: id,
          },
        });
      },
      verifyRedemption(transactions) {
        return _.find(transactions, { origin: 'REDEMPTION' });
      },
      submitAdjustment() {
        const adjustmentObject = {
          amount: parseFloat(this.adjustmentAmount).toFixed(2),
          comment: this.comment };
        this.adjustInterestForNote({
          noteId: this.note.id,
          adjustmentObject,
        }).then(() => {
          this.loadNoteDetail();
          this.showAdjustmentModal = false;
          this.adjustmentWasSuccessfull = true;
        })
        .catch(() => {
          this.showAdjustmentModal = false;
          this.adjustmentWasSuccessfull = false;
          this.setErrorsModalInfo(['We could not process the adjustment.']);
          this.showErrorsModal();
        });
        this.cleanAdjustmentInformation();
      },
      cleanAdjustmentInformation() {
        this.adjustmentAmount = null;
        this.comment = '';
        this.showAdjustmentModal = false;
      },
      ...mapMutations('errors', {
        showErrorsModal: SHOW_ERRORS_MODAL,
        setErrorsModalInfo: SET_ERRORS_MODAL_INFO,
      }),
      ...mapMutations('information', {
        showInformationModal: SHOW_INFORMATION_MODAL,
      }),
      setParams() {
        this.name = this.$route.params.name;
        this.userId = this.$route.params.userId;
        this.noteId = this.$route.params.noteId;
      },
      async loadNoteDetail() {
        this.loading = true;
        return this.getNote(this.noteId)
          .then((note) => {
            this.note = note;
            this.loading = false;
            this.verifyRedeem(note);
            this.hasRedemption(note);
            this.verifyTransfer(note);
            this.verifyAdditionalDeposit(note);
            this.sortTransactionsStrategy('updatedAt');
          })
          .catch((err) => {
            this.loading = false;
            if (err !== 'UNAUTHORIZED') {
              this.setErrorsModalInfo(['We could not retrieve the note detail']);
              this.showErrorsModal();
            }
          });
      },
      async loadData() {
        this.loading = true;
        await this.getUserDetail(this.userId)
          .then(async (user) => {
            this.user = user;
            await this.loadNoteDetail();
          })
          .then(() => {
            this.loading = false;
          })
          .catch((err) => {
            this.loading = false;
            if (err !== 'UNAUTHORIZED') {
              this.setErrorsModalInfo(['We could not retrieve the user detail']);
              this.showErrorsModal();
            }
          });
      },
      canViewRedeemDetail(transaction) {
        return this.note.status === 'open' && transaction.status === 'PRE-PENDING' && (transaction.type === TransactionTypes.SELL_REDEEM_FULL
          || transaction.type === TransactionTypes.SELL_REDEEM_PARTIAL);
      },
      canViewTransferDetail(transaction) {
        return transaction.status === 'PRE-PENDING' && transaction.type === TransactionTypes.ACCT_TRANSFER;
      },
      canViewAddFundsDetail(transaction) {
        return transaction.status === 'PRE-PENDING' && transaction.type === TransactionTypes.ADDITIONAL_DEPOSIT;
      },
      canRerunInterestAccrual(transaction) {
        return transaction.type === TransactionTypes.ACCRINT;
      },
      canTransactionBeFailed(transaction) {
        return (
          this.failableTransactions.includes(transaction.type) &&
          transaction.status === TransactionStatuses.PROCESSED
        );
      },
      verifyRedeem(note) {
        const redemptionExists = _.some(note.transactions, transaction =>
          transaction.status === 'PRE-PENDING' && (transaction.type === TransactionTypes.SELL_REDEEM_FULL
          || transaction.type === TransactionTypes.SELL_REDEEM_PARTIAL));
        const fullRedemptionProccessedExists = _.some(note.transactions, transaction =>
          transaction.status === 'PROCESSED' && (transaction.type === TransactionTypes.SELL_REDEEM_FULL));
        const principalUpcomingTransaction = note.transactions.find(
          t =>
            (t.type === TransactionTypes.SELL_REINVEST ||
              t.type === TransactionTypes.SELL_DONATE ||
              t.type === TransactionTypes.SELL_MATURE) &&
            t.status === TransactionStatuses.PREPENDING,
          );
        const interestUpcomingTransaction = note.transactions.find(
          t =>
            (t.type === TransactionTypes.INT_REINVEST ||
              t.type === TransactionTypes.INT_DONATE ||
              t.type === TransactionTypes.INT_PAID) &&
            t.status === TransactionStatuses.PREPENDING,
        );
        if (
          redemptionExists ||
          note.status !== 'open' ||
          fullRedemptionProccessedExists ||
          (note.account.redemptionRestriction &&
            (principalUpcomingTransaction || interestUpcomingTransaction))
        ) {
          this.isRedeemDisabled = true;
        }
      },
      hasRedemption(note) {
        if (note.transactions.some(t => t.type === TransactionTypes.SELL_REDEEM_FULL ||
        t.type === TransactionTypes.SELL_REDEEM_PARTIAL)) {
          this.isRerunInterestAccrualDisabled = true;
        }
      },
      async handleRerunInterestAccrual(noteId) {
        const isResetedInterestAccrual = await this.resetInterestAccrual(noteId);
        if (isResetedInterestAccrual.ok) {
          this.loadNoteDetail();
        }
      },
      handleRerunInterestAccrualDisabledShowModal() {
        const restrictedMessage = 'Action can\'t be processed because note has a redemption. Please contact support team.';
        this.showInformationModal(restrictedMessage);
      },
      verifyTransfer(note) {
        const transferExists = _.some(note.transactions, transaction =>
          transaction.status === 'PRE-PENDING' && transaction.type === TransactionTypes.ACCT_TRANSFER);
        if (transferExists) {
          this.isTransferDisabled = true;
        }
      },
      verifyAdditionalDeposit(note) {
        const existsSettlement = _.some(note.transactions, transaction =>
          transaction.type === TransactionTypes.SETTLEMENT);

        const existsAddFundsRequest = _.some(note.transactions, transaction =>
          transaction.status === 'PRE-PENDING' && transaction.type === TransactionTypes.ADDITIONAL_DEPOSIT);

        this.isAddFundsDisabled = !existsSettlement || existsAddFundsRequest;
      },
      goToRedeem() {
        const name = this.name;
        const userId = this.userId;
        const noteId = this.noteId;
        const accountId = this.note.accountId;

        const routeParams = { name: 'Redeem',
          params: {
            name,
            userId,
            noteId,
            accountId,
          },
        };
        this.$router.push(routeParams);
      },
      goToTransfer() {
        const name = this.name;
        const userId = this.userId;
        const noteId = this.noteId;

        const routeParams = { name: 'Transfer', params: { name, userId, noteId } };
        this.$router.push(routeParams);
      },
      goToAddFunds() {
        const name = this.name;
        const userId = this.userId;
        const noteId = this.noteId;

        const routeParams = { name: 'AddFunds', params: { name, userId, noteId } };
        this.$router.push(routeParams);
      },
      goToViewDetail() {
        const name = this.name;
        const userId = this.userId;
        const noteId = this.noteId;
        const accountId = this.note.accountId;

        this.$router.push({ name: 'Redeem', params: { name, userId, noteId, accountId } });
      },
      goToViewTransferDetail() {
        const name = this.name;
        const userId = this.userId;
        const noteId = this.noteId;
        this.$router.push({ name: 'Transfer', params: { name, userId, noteId } });
      },
      getWholeAmount(amount) {
        const character = amount < 0 ? '(' : '';
        return `${character}$${Math.abs(amount).toFixed(2).toString().split('.')[0]}`;
      },
      getDecimals(amount) {
        const character = amount < 0 ? ')' : '';
        return `${Math.abs(amount).toFixed(2).toString().split('.')[1]}${character}`;
      },
      openEditCommentModal(transaction) {
        this.transaction = transaction;
        this.isVisibleEditCommentModal = true;
      },
      closeEditCommentModal() {
        this.isVisibleEditCommentModal = false;
        this.transaction = null;
      },
    },
  };
</script>

<style scoped>
  .line-separator {
    padding: 8px;
    line-height: 1.52857143;
    vertical-align: top;
    border-top: 1px solid #eee;
  }
  .left-margin {
    margin-left: 2%;
  }
  .right-margin {
    margin-left: -4%;
  }
  .top-margin {
    margin-top: 2%;
  }
  .bottom-margin {
    margin-bottom: 1%;
  }
  .space {
    margin-top: 4%;
  }
  .content-heading {
    font-weight: normal;
  }
  .panel-title {
    font-size: 20px;
  }
  .panel-subtitle {
    font-size: 16px;
  }
  .panel-default {
    padding: 30px 40px;
  }
  .panel-heading {
    background-color: #ffffff;
    font-size: 16px;
    padding: 0px 5px;
  }
  label {
    color: #888;
    font-size: 14px;
    width: 250px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .table-striped>tbody>tr:nth-of-type(odd) {
    background-color: #fafbfc;
  }
  .table-transaction {
    width: 100%;
  }
  .btn-note-action {
    background-color: #127285;
    margin-right: 10px;
  }
  .btn-back {
    color: #31b0d5;
    background-color: Transparent;
    background-repeat:no-repeat;
    border: none;
    cursor:pointer;
    overflow: hidden;
    outline:none;
    padding-left: 20px;
  }
  .icon-back {
    position:relative;
}
  .icon-back:before {
    content: "\f060";
    font-family: FontAwesome;
    position:absolute;
    left: 0px;
 }
  .title-holder h1 {
    font-size: 16px;
    color: #127285;
    margin-bottom: 20px;
  }
  .adjustment-label {
    color: #535353;
  }
  .row-margin {
    margin-top: 15px;
    margin-bottom: 25px;
  }

  .dollar-sign-holder {
    display: inline-block;
    position: relative;
  }

  .currency {
    padding-left: 5px;
    border: none;
    width: 100%;
    background: transparent;
  }

  .dollar-sign-holder::before {
    content: "$";
    position: absolute;
    left: 5px;
    top: 50%;
    transform: translateY(-50%);
  }

  .whole {
    display: inline-block;
    min-width: 30px !important;
    text-align: right;
  }

  .decimal {
    display: inline-block;
    width: 15px !important;
    text-align: left;
  }

  .aligned-amounts-container {
    display: inline-flex;
  }

  .transaction-comment {
    display: inline-block;
    color: gray;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 56px;
    margin-right: 10px;
    margin-bottom: -3px;
  }

  .fa-pencil {
    font-size: 1.4em;
    margin: -5px -3px;
  }

  .edit-button {
    background-color: #ffffff;
    border-color: lightgray;
    color: #00788d;
  }
  .table-fixed-header--md tbody tr td {
    height: 60px;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
</style>



// WEBPACK FOOTER //
// NoteDetail.vue?10d828c8